import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Separator } from '@/components/ui/separator';
import {
	Search,
	X,
	Grid3X3,
	List,
	Users,
	FolderOpen,
	Play,
	Loader2,
	Globe,
	Lock,
	Clock,
	Sparkles,
	ArrowLeft,
	Plus
} from 'lucide-react';
import { useSavedQueries, useTrackQueryExecution } from '@/hooks/savedQueries';
import { useAuth } from '@/providers/AuthProvider';
import { executeQuery } from '@/utils/queryBuilder';
import { SavedQuery } from '@/types/savedQuery';
import { QueryGroup, QueryCondition, createEmptyGroup, createEmptyCondition, isQueryGroup } from '@/types/queryBuilder';
import { Candidate } from '@/types/candidate';
import { ConditionGroup } from '@/components/queries/ConditionGroup';
import { QueryPreview } from '@/components/queries/QueryPreview';
import { NaturalLanguageQueryDialog } from '@/components/queries/NaturalLanguageQueryDialog';
import { formatDistanceToNow } from 'date-fns';

interface AddFromQueryTabProps {
	selectedCandidates: string[];
	onCandidateToggle: (candidateId: string) => void;
	viewMode: 'grid' | 'list';
	setViewMode: (mode: 'grid' | 'list') => void;
}

type QueryTabView = 'select' | 'saved-query-results' | 'new-query';

export const AddFromQueryTab: React.FC<AddFromQueryTabProps> = ({
	selectedCandidates,
	onCandidateToggle,
	viewMode,
	setViewMode
}) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const [view, setView] = useState<QueryTabView>('select');
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedQuery, setSelectedQuery] = useState<SavedQuery | null>(null);
	const [queryResults, setQueryResults] = useState<Candidate[]>([]);
	const [isLoadingResults, setIsLoadingResults] = useState(false);
	const [isAIOpen, setIsAIOpen] = useState(false);

	// New query builder state
	const [rootGroup, setRootGroup] = useState<QueryGroup>(() => createEmptyGroup('AND'));
	const [hasRun, setHasRun] = useState(false);

	// Saved queries
	const { data: savedQueries = [] } = useSavedQueries();
	const trackExecution = useTrackQueryExecution();

	// Filter queries that have rootGroup (query builder queries)
	const builderQueries = savedQueries.filter(q =>
		q.query_definition && 'rootGroup' in (q.query_definition as any)
	);

	const filteredQueries = builderQueries.filter(q =>
		q.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
		(q.description?.toLowerCase().includes(searchTerm.toLowerCase()))
	);

	// Group queries by ownership
	const myQueries = filteredQueries.filter(q => q.created_by === user?.id);
	const publicQueries = filteredQueries.filter(q => q.is_public && q.created_by !== user?.id);

	const handleLoadSavedQuery = async (query: SavedQuery) => {
		setSelectedQuery(query);
		setView('saved-query-results');
		setIsLoadingResults(true);
		setQueryResults([]);

		try {
			const def = query.query_definition as any;
			if (def?.rootGroup) {
				const results = await executeQuery(def.rootGroup as QueryGroup);
				setQueryResults(results);
				trackExecution.mutate(query.id);
			}
		} catch (error) {
			setQueryResults([]);
		} finally {
			setIsLoadingResults(false);
		}
	};

	const handleCreateNewQuery = () => {
		setView('new-query');
		setRootGroup(createEmptyGroup('AND'));
		setQueryResults([]);
		setHasRun(false);
	};

	const handleBack = () => {
		setView('select');
		setSelectedQuery(null);
		setQueryResults([]);
		setSearchTerm('');
		setHasRun(false);
	};

	// Query builder methods
	const findAndUpdate = (
		group: QueryGroup,
		targetId: string,
		updater: (item: any) => any
	): QueryGroup => {
		const newConditions: any[] = [];

		for (const item of group.conditions) {
			if (item.id === targetId) {
				const updated = updater(item);
				if (updated !== null)
					newConditions.push(updated);
			} else if (isQueryGroup(item)) {
				newConditions.push(findAndUpdate(item, targetId, updater));
			} else {
				newConditions.push(item);
			}
		}

		return { ...group, conditions: newConditions };
	};

	const findAndAddTo = (
		group: QueryGroup,
		parentId: string,
		newItem: any
	): QueryGroup => {
		if (group.id === parentId)
			return { ...group, conditions: [...group.conditions, newItem] };

		const newConditions = group.conditions.map(item => {
			if (isQueryGroup(item))
				return findAndAddTo(item, parentId, newItem);
			return item;
		});

		return { ...group, conditions: newConditions };
	};

	const addCondition = (parentGroupId: string) => {
		const newCondition = createEmptyCondition();
		setRootGroup(prev => findAndAddTo(prev, parentGroupId, newCondition));
	};

	const updateCondition = (conditionId: string, updates: Partial<QueryCondition>) => {
		setRootGroup(prev => findAndUpdate(prev, conditionId, (item) => {
			if (!isQueryGroup(item))
				return { ...item, ...updates };
			return item;
		}));
	};

	const removeCondition = (conditionId: string) => {
		setRootGroup(prev => findAndUpdate(prev, conditionId, () => null));
	};

	const addGroup = (parentGroupId: string, groupType: 'AND' | 'OR') => {
		const newGroup = createEmptyGroup(groupType);
		setRootGroup(prev => findAndAddTo(prev, parentGroupId, newGroup));
	};

	const updateGroupType = (groupId: string, type: 'AND' | 'OR') => {
		setRootGroup(prev => {
			if (prev.id === groupId)
				return { ...prev, type };

			return findAndUpdate(prev, groupId, (item) => {
				if (isQueryGroup(item))
					return { ...item, type };
				return item;
			});
		});
	};

	const removeGroup = (groupId: string) => {
		setRootGroup(prev => findAndUpdate(prev, groupId, () => null));
	};

	const handleRunQuery = async () => {
		setIsLoadingResults(true);
		setHasRun(true);
		try {
			const results = await executeQuery(rootGroup);
			setQueryResults(results);
		} catch (error) {
			setQueryResults([]);
		} finally {
			setIsLoadingResults(false);
		}
	};

	const handleAIQueryGenerated = (query: QueryGroup) => {
		setRootGroup(query);
		setHasRun(false);
		setQueryResults([]);
	};

	const renderCandidateCard = (candidate: Candidate) => (
		<div
			key={candidate.id}
			className={`relative bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-sm cursor-pointer transition-all duration-200 hover:shadow-md ${
				selectedCandidates.includes(candidate.id)
					? 'ring-2 ring-inset ring-blue-500 border-blue-500 p-4'
					: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 p-4'
			}`}
			onClick={() => onCandidateToggle(candidate.id)}
		>
			<div className="absolute top-3 right-3">
				<Checkbox
					checked={selectedCandidates.includes(candidate.id)}
					onChange={() => onCandidateToggle(candidate.id)}
					className="h-4 w-4"
				/>
			</div>

			<div className="flex items-start space-x-3 mb-3">
				<Avatar className="h-10 w-10">
					<AvatarFallback className="bg-blue-100 text-blue-500">
						{candidate.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
					</AvatarFallback>
				</Avatar>
				<div className="flex-1 min-w-0">
					<h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{candidate.name}</h3>
					{candidate.function && (
						<p className="text-sm text-gray-600 dark:text-gray-400 truncate">{candidate.function}</p>
					)}
				</div>
			</div>

			<div className="space-y-1 mb-3">
				{candidate.email && (
					<div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
						<span className="truncate">{candidate.email}</span>
					</div>
				)}
			</div>

			{candidate.skills && candidate.skills.length > 0 && (
				<div className="mb-3">
					<div className="flex flex-wrap gap-1">
						{candidate.skills.slice(0, 2).map((skill: string, index: number) => (
							<Badge key={index} variant="outline" className="text-xs">
								{skill}
							</Badge>
						))}
						{candidate.skills.length > 2 && (
							<Badge variant="outline" className="text-xs">
								+{candidate.skills.length - 2}
							</Badge>
						)}
					</div>
				</div>
			)}

			{candidate.status && (
				<div className="flex items-center">
					<Badge
						variant="outline"
						className={`text-xs ${
							candidate.status.toLowerCase() === 'active'
								? 'bg-green-50 text-green-700 border-green-200'
								: 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
						}`}
					>
						{candidate.status}
					</Badge>
				</div>
			)}
		</div>
	);

	const renderCandidateRow = (candidate: Candidate) => (
		<div
			key={candidate.id}
			className={`flex items-center space-x-4 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-sm cursor-pointer transition-all duration-200 hover:shadow-sm ${
				selectedCandidates.includes(candidate.id)
					? 'ring-2 ring-inset ring-blue-500 border-blue-500 p-4'
					: 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 p-4'
			}`}
			onClick={() => onCandidateToggle(candidate.id)}
		>
			<Checkbox
				checked={selectedCandidates.includes(candidate.id)}
				onChange={() => onCandidateToggle(candidate.id)}
				className="h-4 w-4"
			/>

			<Avatar className="h-8 w-8">
				<AvatarFallback className="bg-blue-100 text-blue-500 text-xs">
					{candidate.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
				</AvatarFallback>
			</Avatar>

			<div className="flex-1 min-w-0">
				<h3 className="font-semibold text-gray-900 dark:text-gray-100">{candidate.name}</h3>
				{candidate.function && (
					<p className="text-sm text-gray-600 dark:text-gray-400">{candidate.function}</p>
				)}
			</div>

			<div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
				{candidate.email && <span className="truncate max-w-32">{candidate.email}</span>}
			</div>

			{candidate.skills && candidate.skills.length > 0 && (
				<div className="flex flex-wrap gap-1">
					{candidate.skills.slice(0, 3).map((skill: string, index: number) => (
						<Badge key={index} variant="outline" className="text-xs">
							{skill}
						</Badge>
					))}
					{candidate.skills.length > 3 && (
						<Badge variant="outline" className="text-xs">
							+{candidate.skills.length - 3}
						</Badge>
					)}
				</div>
			)}

			{candidate.status && (
				<Badge
					variant="outline"
					className={`text-xs ${
						candidate.status.toLowerCase() === 'active'
							? 'bg-green-50 text-green-700 border-green-200'
							: 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
					}`}
				>
					{candidate.status}
				</Badge>
			)}
		</div>
	);

	const renderResults = () => {
		if (isLoadingResults) {
			return (
				<div className="p-8 text-center">
					<div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
					<p className="text-gray-500 dark:text-gray-400">{t("jobs.kanban.query.runningQuery", "Running query...")}</p>
				</div>
			);
		}

		if (queryResults.length === 0) {
			return (
				<div className="p-8 text-center">
					<Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
					<h3 className="text-lg font-semibold mb-2">
						{hasRun || view === 'saved-query-results'
							? t("jobs.kanban.query.noResults", "No candidates found")
							: t("jobs.kanban.query.runToSeeResults", "Run the query to see results")}
					</h3>
					<p className="text-gray-500 dark:text-gray-400">
						{hasRun || view === 'saved-query-results'
							? t("jobs.kanban.query.adjustQuery", "Try adjusting your query criteria")
							: t("jobs.kanban.query.buildAndRun", "Build your query and click Run to find candidates")}
					</p>
				</div>
			);
		}

		return viewMode === 'grid' ? (
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
				{queryResults.map(renderCandidateCard)}
			</div>
		) : (
			<div className="space-y-2 p-4">
				{queryResults.map(renderCandidateRow)}
			</div>
		);
	};

	// Select view - choose between saved query or new query
	if (view === 'select') {
		return (
			<div className="flex-1 flex flex-col space-y-4 min-h-0 overflow-hidden">
				{/* Option Cards */}
				<div className="px-4 py-2">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{/* Use Saved Query Card */}
						<Card
							className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-2 border-dashed hover:border-blue-300 dark:hover:border-blue-500"
							onClick={() => {}}
						>
							<CardContent className="p-6 text-center">
								<FolderOpen className="h-12 w-12 text-blue-500 mx-auto mb-3" />
								<h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
									{t("jobs.kanban.query.useSavedQuery", "Use Saved Query")}
								</h3>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{t("jobs.kanban.query.useSavedQueryDesc", "Select from your previously saved queries")}
								</p>
							</CardContent>
						</Card>

						{/* Create New Query Card */}
						<Card
							className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-2 border-dashed hover:border-purple-300 dark:hover:border-purple-600"
							onClick={handleCreateNewQuery}
						>
							<CardContent className="p-6 text-center">
								<Plus className="h-12 w-12 text-purple-500 mx-auto mb-3" />
								<h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
									{t("jobs.kanban.query.createNewQuery", "Create New Query")}
								</h3>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									{t("jobs.kanban.query.createNewQueryDesc", "Build a custom query to find candidates")}
								</p>
							</CardContent>
						</Card>
					</div>
				</div>

				<Separator />

				{/* Saved Queries List */}
				<div className="px-4">
					<h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
						{t("jobs.kanban.query.savedQueries", "Saved Queries")}
					</h3>

					{/* Search */}
					<div className="relative mb-4">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
						<Input
							placeholder={t("jobs.kanban.query.searchQueries", "Search queries...")}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="pl-10 pr-10 h-10"
						/>
						{searchTerm && (
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
								onClick={() => setSearchTerm('')}
							>
								<X className="h-4 w-4" />
							</Button>
						)}
					</div>
				</div>

				{/* Queries List */}
				<div className="flex-1 overflow-y-auto px-4 min-h-0">
					{filteredQueries.length === 0 ? (
						<div className="text-center py-8">
							<Search className="h-12 w-12 mx-auto mb-4 text-gray-300" />
							<p className="text-gray-500 dark:text-gray-400 font-medium">
								{t("jobs.kanban.query.noQueriesFound", "No saved queries found")}
							</p>
							<p className="text-gray-400 text-sm mt-1">
								{t("jobs.kanban.query.createOneFirst", "Create a query first to see it here")}
							</p>
						</div>
					) : (
						<div className="space-y-4">
							{/* My Queries */}
							{myQueries.length > 0 && (
								<div>
									<div className="flex items-center gap-2 mb-2">
										<Badge variant="secondary" className="text-xs font-medium">
											{t("jobs.kanban.query.myQueries", "My Queries")}
										</Badge>
										<span className="text-xs text-gray-400">({myQueries.length})</span>
									</div>
									<div className="space-y-2">
										{myQueries.map((query) => (
											<Card
												key={query.id}
												className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500"
												onClick={() => handleLoadSavedQuery(query)}
											>
												<CardContent className="p-4">
													<div className="flex items-start justify-between gap-3">
														<div className="flex-1 min-w-0">
															<div className="flex items-center gap-2 mb-1">
																{query.is_public ? (
																	<Globe className="h-4 w-4 text-blue-500 shrink-0" />
																) : (
																	<Lock className="h-4 w-4 text-gray-400 shrink-0" />
																)}
																<span className="font-medium text-gray-900 dark:text-gray-100 truncate">{query.name}</span>
															</div>
															{query.description && (
																<p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1 mb-2">
																	{query.description}
																</p>
															)}
															<div className="flex items-center gap-3 text-xs text-gray-400">
																<span className="flex items-center gap-1">
																	<Play className="h-3 w-3" />
																	{query.execution_count} {t("queries.load.times", "times")}
																</span>
																{query.last_executed_at && (
																	<span className="flex items-center gap-1">
																		<Clock className="h-3 w-3" />
																		{formatDistanceToNow(new Date(query.last_executed_at), { addSuffix: true })}
																	</span>
																)}
															</div>
														</div>
														<Button size="sm" variant="outline" className="shrink-0">
															<Play className="h-4 w-4 mr-1" />
															{t("jobs.kanban.query.run", "Run")}
														</Button>
													</div>
												</CardContent>
											</Card>
										))}
									</div>
								</div>
							)}

							{/* Public Queries */}
							{publicQueries.length > 0 && (
								<div>
									<div className="flex items-center gap-2 mb-2">
										<Badge variant="outline" className="text-xs font-medium">
											{t("jobs.kanban.query.publicQueries", "Public Queries")}
										</Badge>
										<span className="text-xs text-gray-400">({publicQueries.length})</span>
									</div>
									<div className="space-y-2">
										{publicQueries.map((query) => (
											<Card
												key={query.id}
												className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500"
												onClick={() => handleLoadSavedQuery(query)}
											>
												<CardContent className="p-4">
													<div className="flex items-start justify-between gap-3">
														<div className="flex-1 min-w-0">
															<div className="flex items-center gap-2 mb-1">
																<Globe className="h-4 w-4 text-blue-500 shrink-0" />
																<span className="font-medium text-gray-900 dark:text-gray-100 truncate">{query.name}</span>
															</div>
															{query.description && (
																<p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1 mb-2">
																	{query.description}
																</p>
															)}
															<div className="flex items-center gap-3 text-xs text-gray-400">
																<span className="flex items-center gap-1">
																	<Play className="h-3 w-3" />
																	{query.execution_count} {t("queries.load.times", "times")}
																</span>
															</div>
														</div>
														<Button size="sm" variant="outline" className="shrink-0">
															<Play className="h-4 w-4 mr-1" />
															{t("jobs.kanban.query.run", "Run")}
														</Button>
													</div>
												</CardContent>
											</Card>
										))}
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		);
	}

	// Saved query results view
	if (view === 'saved-query-results') {
		return (
			<div className="flex-1 flex flex-col space-y-4 min-h-0 overflow-hidden">
				{/* Header */}
				<div className="flex items-center gap-4 px-4 py-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={handleBack}
						className="flex items-center gap-2"
					>
						<ArrowLeft className="h-4 w-4" />
						{t("common.back", "Back")}
					</Button>
					<div className="flex-1">
						<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
							{selectedQuery?.name}
						</h3>
						<p className="text-sm text-gray-600 dark:text-gray-400">
							{queryResults.length} {t("jobs.kanban.query.candidatesFound", "candidates found")}
						</p>
					</div>
				</div>

				{/* View Toggle */}
				<div className="flex items-center justify-end gap-2 px-4">
					<span className="text-sm text-gray-500 dark:text-gray-400">{t("common.view", "View")}:</span>
					<ToggleGroup
						type="single"
						value={viewMode}
						onValueChange={(value) => value && setViewMode(value as 'grid' | 'list')}
						className="h-8 bg-gray-50 dark:bg-gray-800 rounded-sm p-1"
					>
						<ToggleGroupItem
							value="grid"
							className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
						>
							<Grid3X3 className="h-4 w-4" />
						</ToggleGroupItem>
						<ToggleGroupItem
							value="list"
							className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
						>
							<List className="h-4 w-4" />
						</ToggleGroupItem>
					</ToggleGroup>
				</div>

				{/* Results */}
				<div className="flex-1 overflow-y-auto border dark:border-gray-700 rounded-sm bg-gray-50 dark:bg-gray-800/50 min-h-0">
					{renderResults()}
				</div>
			</div>
		);
	}

	// New query builder view
	return (
		<div className="flex-1 flex flex-col min-h-0 overflow-hidden">
			{/* Header - Fixed */}
			<div className="flex items-center gap-4 px-4 py-2 flex-shrink-0">
				<Button
					variant="ghost"
					size="sm"
					onClick={handleBack}
					className="flex items-center gap-2"
				>
					<ArrowLeft className="h-4 w-4" />
					{t("common.back", "Back")}
				</Button>
				<div className="flex-1">
					<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
						{t("jobs.kanban.query.buildQuery", "Build Query")}
					</h3>
					<p className="text-sm text-gray-600 dark:text-gray-400">
						{t("jobs.kanban.query.buildQueryDesc", "Create a query to find matching candidates")}
					</p>
				</div>
			</div>

			{/* Scrollable Content Area */}
			<div className="flex-1 overflow-y-auto min-h-0 px-4 pb-4">
				{/* Query Builder */}
				<div className="space-y-4">
					<ConditionGroup
						group={rootGroup}
						isRoot
						onTypeChange={(type) => updateGroupType(rootGroup.id, type)}
						onAddCondition={() => addCondition(rootGroup.id)}
						onAddGroup={(type) => addGroup(rootGroup.id, type)}
						onUpdateCondition={updateCondition}
						onRemoveCondition={removeCondition}
						onUpdateGroupType={updateGroupType}
						onRemoveGroup={removeGroup}
						onAddConditionToGroup={addCondition}
						onAddGroupToGroup={addGroup}
					/>

					{/* Query Preview */}
					<div className="space-y-2">
						<h4 className="text-sm font-medium text-muted-foreground">
							{t("queries.preview.title", "Query Preview")}
						</h4>
						<QueryPreview
							group={rootGroup}
							count={queryResults.length}
							isLoading={isLoadingResults}
							hasRun={hasRun}
						/>
					</div>

					{/* Action Buttons */}
					<div className="flex flex-wrap gap-2">
						<Button onClick={handleRunQuery} disabled={isLoadingResults}>
							{isLoadingResults ? (
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
							) : (
								<Play className="h-4 w-4 mr-2" />
							)}
							{t("queries.actions.run", "Run Query")}
						</Button>

						<Button
							variant="outline"
							onClick={() => setIsAIOpen(true)}
							className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-300 hover:border-purple-400 hover:bg-gradient-to-r hover:from-purple-500/20 hover:to-pink-500/20"
						>
							<Sparkles className="h-4 w-4 mr-2 text-purple-500" />
							{t("queries.actions.aiQuery", "AI Query")}
						</Button>
					</div>
				</div>

				<Separator className="my-4" />

				{/* View Toggle */}
				{hasRun && queryResults.length > 0 && (
					<div className="flex items-center justify-between mb-4">
						<span className="text-sm text-gray-600 dark:text-gray-400">
							{queryResults.length} {t("jobs.kanban.query.candidatesFound", "candidates found")}
						</span>
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-500 dark:text-gray-400">{t("common.view", "View")}:</span>
							<ToggleGroup
								type="single"
								value={viewMode}
								onValueChange={(value) => value && setViewMode(value as 'grid' | 'list')}
								className="h-8 bg-gray-50 dark:bg-gray-800 rounded-sm p-1"
							>
								<ToggleGroupItem
									value="grid"
									className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
								>
									<Grid3X3 className="h-4 w-4" />
								</ToggleGroupItem>
								<ToggleGroupItem
									value="list"
									className="h-6 w-6 p-0 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
								>
									<List className="h-4 w-4" />
								</ToggleGroupItem>
							</ToggleGroup>
						</div>
					</div>
				)}

				{/* Results */}
				<div className="border dark:border-gray-700 rounded-sm bg-gray-50 dark:bg-gray-800/50">
					{renderResults()}
				</div>
			</div>

			{/* AI Query Dialog */}
			<NaturalLanguageQueryDialog
				open={isAIOpen}
				onOpenChange={setIsAIOpen}
				onQueryGenerated={handleAIQueryGenerated}
			/>
		</div>
	);
};
